import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
// import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown"; // Poolcorp Custom Code
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

/* Poolcorp Custom Code - Start */
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@store/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { css } from "styled-components";
/* Poolcorp Custom Code - End */

interface OwnProps {
    noShipToAndCantCreate: boolean;
    setParameter: (parameter: GetBillTosApiParameter) => void;
    parameter: GetBillTosApiParameter;
    billTo?: BillToModel;
    onSelect: (billTo: BillToModel) => void; // Poolcorp Custom Code
    extendedStyles?: ChangeCustomerBillToSelectorStyles;
    isLoading?: boolean;
}

const mapStateToProps = (state: StoreApplicationState, props: OwnProps) => ({
    billTosDataView: getBillTosDataView(state, props.parameter),
    customerSettings: getSettingsCollection(state).customerSettings,
    // Poolcorp Custom Code
    currentSelectedBillTo: state.store.pages.changeCustomer.currentSelectedBillTo,
});

const mapDispatchToProps = {
    loadBillTos,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ChangeCustomerBillToSelectorStyles {
    billToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
}

/* Poolcorp Custom Code - Start */
export const changeCustomerBillToSelectorStyles: ChangeCustomerBillToSelectorStyles = {
    billToSelectorDynamicDropdown: {
        clearClickable: {
            css: css`
                right: 43px !important;
            `,
        },
    },
};
/* Poolcorp Custom Code - End */

const ChangeCustomerBillToSelector: FC<Props> = ({
    noShipToAndCantCreate,
    parameter,
    setParameter,
    billTo,
    onSelect,
    extendedStyles,
    isLoading,
    billTosDataView,
    // Poolcorp Custom Code
    currentSelectedBillTo
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerBillToSelectorStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [hasClear, setHasClear] = useState(false); // Poolcorp Custom Code

    useEffect(() => {
        if (billTosDataView.value) {
            //  Poolcorp Custom Code
            const sortedBillTos = billTosDataView.value.slice().sort((a: {companyName: string},b: {companyName:string}) => 
                a.companyName.localeCompare(b.companyName)
            );
            const options: OptionObject[] = sortedBillTos.map(billTo => ({
                optionText: billTo.label,
                optionValue: billTo.id,
            }));
            setOptions(options);
        }

        const BillTo = billTo?.id && currentSelectedBillTo?.id !== undefined;
        if (BillTo) {
            setSelectedBillTo(billTo as BillToModel);
        }
        
    }, [billTosDataView]);

    /* Poolcorp Custom Code */
    const [executedOnce, setExecutedOnce] = useState(false);
    
    useEffect(() => {
        if (options && !executedOnce && billTosDataView.value) {
            let defaultBillTo:BillToModel | undefined;
            billTosDataView.value.forEach(billTo => {
                if (billTo.properties['isDefaultBillToLogin'] === "True") {
                    defaultBillTo = billTo;
                }
            });
            if(defaultBillTo) {
                setSelectedBillTo(defaultBillTo as BillToModel);
            }
            setExecutedOnce(true);
        }
    }, [options, executedOnce, billTosDataView.value]);

    const setSelectedBillTo = (billTo: BillToModel) => {
        onSelect(billTo);
    };
    /* Poolcorp Custom Code */

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setParameter({
            ...parameter,
            filter: event.currentTarget.value,
        });
        setHasClear(!!(event.currentTarget.value && event.currentTarget.value !== "")); // Poolcorp Custom Code
    };

    const selectCustomerHandler = (billToId?: string) => {
        if (billTosDataView.value && billToId) {
            const billTo = billTosDataView.value.filter(billTo => billTo.id === billToId)[0];
            if (billTo) {
                setSelectedBillTo(billTo as BillToModel); // Poolcorp Custom Code
            }
        }
    };

    /* Poolcorp Custom Code */
    const clearButtonHandler = () => {
        setParameter({
            ...parameter,
            filter: "",
        });
        setHasClear(false);
    };

    const onOpen = (event?: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | undefined) => {
        setHasClear(false);
        setParameter({
            ...parameter,
            filter: "",
        });
    };

    const onClose = (event?: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | undefined) => {
        setHasClear(false);
    };
    /* Poolcorp Custom Code */

    return (
        <DynamicDropdown
            label={translate("Select Bill To")}
            {...styles.billToSelectorDynamicDropdown}
            onSelectionChange={selectCustomerHandler}
            onInputChange={searchTextChanged}
            selected={billTo?.id || currentSelectedBillTo?.id} // Poolcorp Custom Code
            options={options}
            placeholder={translate("Search or Select Bill To")}
            isLoading={isLoading}
            error={noShipToAndCantCreate && siteMessage("SignIn_NoShipToAndCantCreate")}
            data-test-selector="changeCustomerBillToSelector"
            filterOption={() => true}
            // Poolcorp Custom Code
            showClear={hasClear}
            clearOnClick={clearButtonHandler}
            onOpen={onOpen}
            onClose={onClose}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeCustomerBillToSelector);
